import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, Receipt, RefreshCcw, Search, ShieldCheck, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { EmptyState, PageHeader, Spinner } from "../../components/common";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { PaginationBar, TableRowsSkeleton, usePagedList } from "../../components/PaginationBar";
import { DeleteReceiptDialog, RESTORE_WINDOW_DAYS } from "../../components/DeleteReceiptDialog";
import { DeletedReceiptsTab } from "../../components/DeletedReceiptsTab";
import { api, errMsg, fmtDate, fmtINR } from "../../lib/api";

const ALL = "__all__";

/**
 * Phase 177 — Super Admin › Receipts.
 *   Receipts tab          live receipts of ONE municipality (search / date range required) with a Delete action
 *   Deleted receipts tab  every soft-deleted receipt across ALL municipalities (or one), restore inside the 30-day
 *                         window, permanent delete once the window has closed (Super Admin only).
 */
export default function SuperReceipts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [munis, setMunis] = useState(null);
  const code = searchParams.get("muni") || ALL;
  const tab = searchParams.get("tab") === "receipts" ? "receipts" : "deleted";
  const setParam = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => { if (v && v !== ALL) next.set(k, v); else next.delete(k); });
    setSearchParams(next, { replace: true });
  };
  // live receipts filter (fetch-gated: nothing loads until Search)
  const [draft, setDraft] = useState({ search: "", date_from: "", date_to: "" });
  const [applied, setApplied] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    api.get("/platform/municipalities")
      .then((r) => setMunis((Array.isArray(r.data) ? r.data : r.data.items || []).filter((m) => m.is_active !== false)))
      .catch((e) => { toast.error(errMsg(e)); setMunis([]); });
  }, []);
  const muni = useMemo(() => (munis || []).find((m) => m.code === code) || null, [munis, code]);
  const hasMuni = code !== ALL;

  const liveParams = useMemo(() => {
    const p = {};
    Object.entries(applied || {}).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [applied]);
  const live = usePagedList(`/platform/municipalities/${code}/receipts`, { params: liveParams, initialLimit: 25,
    enabled: tab === "receipts" && hasMuni && !!applied, reloadKey });

  const runSearch = (e) => {
    e?.preventDefault?.();
    if (!draft.search.trim() && !draft.date_from && !draft.date_to) return toast.error("Enter a receipt number / PIN / owner or choose a date range");
    setApplied({ ...draft });
  };
  useEffect(() => { setApplied(null); }, [code]);

  return (
    <div className="space-y-5 pb-24" data-testid="super-receipts-page">
      <PageHeader title="Receipts"
        subtitle={`Delete payment receipts of any municipality and manage the recycle bin: deleted receipts stay restorable for ${RESTORE_WINDOW_DAYS} days, after which only you can remove them permanently.`}>
        <Button variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)} data-testid="super-receipts-refresh-button">
          <RefreshCcw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </PageHeader>

      <Card className="shadow-sm">
        <CardContent className="p-4 sm:p-5 flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="space-y-1.5 flex-1 max-w-md">
            <Label>Municipality</Label>
            {!munis ? <Spinner label="Loading municipalities..." /> : (
              <Select value={code} onValueChange={(v) => setParam({ muni: v })}>
                <SelectTrigger data-testid="super-receipts-muni-select"><SelectValue placeholder="Select a municipality" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL} data-testid="super-receipts-muni-option-all">All municipalities (deleted receipts)</SelectItem>
                  {munis.map((m) => (
                    <SelectItem key={m.code} value={m.code} data-testid={`super-receipts-muni-option-${m.code}`}>
                      {m.name} <span className="text-muted-foreground font-mono text-xs">({m.code})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <Tabs value={tab} onValueChange={(v) => setParam({ tab: v === "deleted" ? "" : v })}>
            <TabsList>
              <TabsTrigger value="deleted" className="gap-1.5" data-testid="super-receipts-tab-deleted"><Trash2 className="h-3.5 w-3.5" /> Deleted receipts</TabsTrigger>
              <TabsTrigger value="receipts" className="gap-1.5" data-testid="super-receipts-tab-receipts"><Receipt className="h-3.5 w-3.5" /> Receipts</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="text-xs bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] rounded-md px-3 py-2 flex gap-2 flex-1" data-testid="super-receipts-note">
            <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              You always hold the <span className="font-semibold">Delete receipts</span> permission. Municipal Admins get it from
              Access Control → Municipal Admin → MIS Reports → <i>Delete receipts</i> (role default per municipality, or per user).
            </span>
          </div>
        </CardContent>
      </Card>

      {tab === "deleted" ? (
        <DeletedReceiptsTab path="/platform/deleted-receipts" extraParams={hasMuni ? { code } : {}} codeOf={(row) => row.municipality?.code}
          showMunicipality={!hasMuni} reloadKey={reloadKey} testId="super-deleted-receipts" />
      ) : !hasMuni ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <EmptyState title="Pick a municipality to search its receipts" subtitle="Live receipts are listed per municipality — choose one above, then search by receipt number, PIN, owner or date." />
        </CardContent></Card>
      ) : (
        <>
          <Card className="shadow-sm">
            <form onSubmit={runSearch} data-testid="super-receipts-filter-form">
              <CardContent className="p-3 sm:p-4 flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input className="pl-9 h-9" placeholder="Receipt no, PIN, owner, UTR, cheque no…" value={draft.search}
                    onChange={(e) => setDraft({ ...draft, search: e.target.value })} data-testid="super-receipts-search-input" />
                </div>
                <div className="flex items-center gap-2">
                  <Input type="date" className="h-9 w-[150px]" value={draft.date_from} max={draft.date_to || undefined}
                    onChange={(e) => setDraft({ ...draft, date_from: e.target.value })} aria-label="From date" data-testid="super-receipts-date-from" />
                  <span className="text-xs text-muted-foreground">to</span>
                  <Input type="date" className="h-9 w-[150px]" value={draft.date_to} min={draft.date_from || undefined}
                    onChange={(e) => setDraft({ ...draft, date_to: e.target.value })} aria-label="To date" data-testid="super-receipts-date-to" />
                </div>
                <Button type="submit" size="sm" className="h-9" data-testid="super-receipts-search-button"><Search className="h-3.5 w-3.5 mr-1.5" /> Search</Button>
                {(draft.search || draft.date_from || draft.date_to) && (
                  <Button type="button" variant="ghost" size="sm" className="h-9" onClick={() => { setDraft({ search: "", date_from: "", date_to: "" }); setApplied(null); }} data-testid="super-receipts-clear-button">
                    <X className="h-3.5 w-3.5 mr-1" /> Clear
                  </Button>
                )}
              </CardContent>
            </form>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-0">
              {!applied ? (
                <EmptyState title={`Search ${muni?.name || "the municipality"}'s receipts`} subtitle="Enter a receipt number, PIN or owner name — or choose a date range — then click Search." />
              ) : live.error && !live.data ? (
                <div className="flex flex-col items-center justify-center gap-2 py-12 text-center" data-testid="super-receipts-error">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                  <p className="text-sm text-muted-foreground">{errMsg(live.error)}</p>
                  <Button size="sm" variant="outline" onClick={() => live.reload()}>Retry</Button>
                </div>
              ) : !live.data ? <TableRowsSkeleton rows={6} cols={7} testId="super-receipts-skeleton" /> : live.items.length === 0 ? (
                <EmptyState title="No receipts match" subtitle="Try another receipt number, PIN or a wider date range." />
              ) : (
                <>
                  <div className={`overflow-x-auto ${live.loading ? "opacity-60 transition-opacity" : ""}`}>
                    <Table data-testid="super-receipts-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Receipt</TableHead>
                          <TableHead>Property</TableHead>
                          <TableHead className="hidden md:table-cell">Ward</TableHead>
                          <TableHead>Mode</TableHead>
                          <TableHead className="hidden lg:table-cell">Collected by</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="w-12 text-right"><span className="sr-only">Actions</span></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {live.items.map((p) => (
                          <TableRow key={p.id} data-testid={`super-receipt-row-${p.receipt_number || p.id}`}>
                            <TableCell className="whitespace-nowrap text-sm tabular-nums">{fmtDate(p.payment_date)}</TableCell>
                            <TableCell className="font-mono text-sm whitespace-nowrap">
                              {p.receipt_number}
                              {p.is_import && <Badge variant="outline" className="ml-1.5 text-[10px] font-normal bg-violet-50 text-violet-800 border-violet-200">Imported</Badge>}
                            </TableCell>
                            <TableCell>
                              <p className="font-mono text-sm">{p.pin || "—"}</p>
                              <p className="text-xs text-muted-foreground truncate max-w-[180px]">{p.owner_name || ""}</p>
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-sm">
                              <p>{p.ward_label || "—"}</p>
                              {p.zone_label && <p className="text-xs text-muted-foreground">{p.zone_label}</p>}
                            </TableCell>
                            <TableCell><Badge variant="outline" className="font-medium">{p.payment_mode || "Unknown"}</Badge></TableCell>
                            <TableCell className="hidden lg:table-cell text-sm">{p.collected_by_name || (p.is_import ? "Imported (Legacy)" : "Online / Citizen")}</TableCell>
                            <TableCell className="text-right tabular-nums font-medium whitespace-nowrap">{fmtINR(p.amount)}</TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" title="Delete this receipt"
                                onClick={() => setDeleting(p)} data-testid={`super-receipt-delete-${p.receipt_number || p.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="px-4 pb-3 border-t">
                    <PaginationBar page={live.page} limit={live.limit} total={live.total} loading={live.loading}
                      onPageChange={live.setPage} onLimitChange={live.setLimit} testId="super-receipts-pagination" />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <DeleteReceiptDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)} receipt={deleting} code={deleting?.municipality?.code || code}
        onDeleted={() => { live.reload(); setReloadKey((k) => k + 1); }} testId="super-delete-receipt" />
    </div>
  );
}
